// /opt/isp_erp/frontend/src/components/warehouse/ProveedoresPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert, CircularProgress, TablePagination,
  FormControl, InputLabel, Select, MenuItem, Divider, InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, Block, CheckCircle, Refresh, Search } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const SECCIONES = [
  {
    titulo: 'Datos generales',
    campos: [
      { key: 'nombre', label: 'Nombre comercial *', md: 6 },
      { key: 'razon_social', label: 'Razón social', md: 6 },
      { key: 'ruc', label: 'RUC / Identificación', md: 6 },
      { key: 'ciudad', label: 'Ciudad', md: 6 }
    ]
  },
  {
    titulo: 'Contacto',
    campos: [
      { key: 'contacto', label: 'Persona de contacto', md: 6 },
      { key: 'email', label: 'Correo electrónico', md: 6 },
      { key: 'telefono', label: 'Teléfono', md: 6 },
      { key: 'telefono_alterno', label: 'Teléfono alterno', md: 6 }
    ]
  },
  {
    titulo: 'Ubicación y condiciones comerciales',
    campos: [
      { key: 'direccion', label: 'Dirección', md: 12 },
      { key: 'condiciones_pago', label: 'Condiciones de pago (ej: contado, crédito a 30 días)', md: 12 },
      { key: 'observaciones', label: 'Observaciones', md: 12, multiline: true }
    ]
  }
];
const CAMPOS = SECCIONES.flatMap((s) => s.campos);
const VACIO = Object.fromEntries(CAMPOS.map((c) => [c.key, '']));

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return porDefecto;
};

const ProveedoresPanel = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');
  const [aEliminar, setAEliminar] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(texto.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage };
      if (busqueda) params.q = busqueda;
      if (filtroActivo === 'activos') params.activo = true;
      if (filtroActivo === 'inactivos') params.activo = false;
      const r = await warehouseAPI.listarProveedores(params);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(detalleError(err, 'Error al cargar los proveedores'));
    } finally {
      setLoading(false);
    }
  }, [busqueda, filtroActivo, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const avisar = (txt) => {
    setMensaje(txt);
    setTimeout(() => setMensaje(''), 4000);
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm(VACIO);
    setErrorDialogo('');
    setDialogOpen(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm(Object.fromEntries(CAMPOS.map((c) => [c.key, p[c.key] || ''])));
    setErrorDialogo('');
    setDialogOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      setErrorDialogo('El nombre del proveedor es obligatorio');
      return;
    }
    setGuardando(true);
    setErrorDialogo('');
    try {
      if (editando) {
        await warehouseAPI.actualizarProveedor(editando.id, form);
        avisar('Proveedor actualizado');
      } else {
        await warehouseAPI.crearProveedor(form);
        avisar('Proveedor creado');
      }
      setDialogOpen(false);
      cargar();
    } catch (err) {
      setErrorDialogo(detalleError(err, 'No se pudo guardar el proveedor'));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarActivo = async (p) => {
    try {
      await warehouseAPI.actualizarProveedor(p.id, { activo: !p.activo });
      avisar(p.activo ? 'Proveedor desactivado' : 'Proveedor activado');
      cargar();
    } catch (err) {
      setError(detalleError(err, 'No se pudo cambiar el estado'));
    }
  };

  const eliminar = async () => {
    try {
      await warehouseAPI.eliminarProveedor(aEliminar.id);
      setAEliminar(null);
      avisar('Proveedor eliminado');
      cargar();
    } catch (err) {
      setAEliminar(null);
      setError(detalleError(err, 'No se pudo eliminar el proveedor'));
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6">Proveedores</Typography>
            <Typography variant="body2" color="text.secondary">
              Registra a quién le compras materiales y equipos. {total} proveedor(es) en total.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={abrirNuevo}>
            Nuevo proveedor
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Buscar por nombre, RUC o contacto"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={filtroActivo}
                  onChange={(e) => {
                    setFiltroActivo(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="activos">Activos</MenuItem>
                  <MenuItem value="inactivos">Inactivos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={cargar}>
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Proveedor</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Contacto</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Ciudad y condiciones</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Uso</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Estado</TableCell>
                <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No hay proveedores registrados</TableCell>
                </TableRow>
              ) : (
                items.map((p) => (
                  <TableRow key={p.id} hover sx={{ opacity: p.activo ? 1 : 0.6 }}>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="body2" fontWeight="bold">{p.nombre}</Typography>
                      {p.razon_social && (
                        <Typography variant="caption" color="text.secondary" display="block">{p.razon_social}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">RUC: {p.ruc || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2">{p.contacto || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[p.telefono, p.telefono_alterno].filter(Boolean).join(' / ') || 'Sin teléfono'}
                      </Typography>
                      {p.email && (
                        <Typography variant="caption" color="text.secondary" display="block">{p.email}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2">{p.ciudad || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.condiciones_pago || 'Sin condiciones registradas'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" variant="outlined" label={`Materiales: ${p.total_materiales}`} />
                        <Chip size="small" variant="outlined" label={`Equipos: ${p.total_equipos}`} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={p.activo ? 'success' : 'default'} label={p.activo ? 'Activo' : 'Inactivo'} />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(p)}><Edit /></IconButton>
                      </Tooltip>
                      <Tooltip title={p.activo ? 'Desactivar' : 'Activar'}>
                        <IconButton size="small" onClick={() => cambiarActivo(p)}>
                          {p.activo ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => setAEliminar(p)}><Delete /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => !guardando && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
        <DialogContent dividers>
          {errorDialogo && <Alert severity="error" sx={{ mb: 2 }}>{errorDialogo}</Alert>}
          {SECCIONES.map((s, idx) => (
            <Box key={s.titulo} sx={{ mb: idx < SECCIONES.length - 1 ? 3 : 0 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>{s.titulo}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {s.campos.map((c) => (
                  <Grid item xs={12} md={c.md} key={c.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={c.label}
                      value={form[c.key]}
                      multiline={!!c.multiline}
                      rows={c.multiline ? 2 : undefined}
                      onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!aEliminar} onClose={() => setAEliminar(null)}>
        <DialogTitle>Eliminar proveedor</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar a <strong>{aEliminar?.nombre}</strong>? Si ya tiene materiales, equipos o ingresos, el sistema no lo
            permitirá y deberás desactivarlo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAEliminar(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={eliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ProveedoresPanel;
